import { Link } from '@tanstack/react-router';
import { Check, ChevronsUpDown, EyeOff, Plus, Settings2, Sparkles, X } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ProductCollectionMembership } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { ProductCollectionsDraft } from '../hooks/use-product-collections-draft';

export interface ProductCollectionsCardProps {
  /** The page's draft (`useProductCollectionsDraft`); the page's Save writes it. */
  draft: ProductCollectionsDraft;
  storeName: string;
  /** The master product id. */
  productId: string;
  /** Rendered above the pills, e.g. a store picker when the organisation has several stores. */
  storeSelector?: ReactNode;
  /**
   * Called by the button of the "not published" state. Without it the button links to the
   * master product's page, where the Stores card publishes it.
   */
  onPublish?: () => void;
  /** `data-tour` value of the card. */
  tour?: string;
  /** Beside the title: the store page's This store chip. */
  badge?: ReactNode;
}

function Unpublished({
  storeName,
  productId,
  onPublish,
}: {
  storeName: string;
  productId: string;
  onPublish: (() => void) | undefined;
}) {
  const { t } = useTranslation('collections');
  return (
    <div className="flex flex-col items-start gap-2 rounded-md border border-dashed p-3">
      <p className="text-muted-foreground text-xs">
        {t('membership.unpublished', { store: storeName })}
      </p>
      {onPublish ? (
        <Button type="button" size="sm" variant="outline" onClick={onPublish}>
          {t('membership.goToPublish')}
        </Button>
      ) : (
        <Button asChild size="sm" variant="outline">
          <Link to="/org/products/$productId" params={{ productId }}>
            {t('membership.goToPublish')}
          </Link>
        </Button>
      )}
    </div>
  );
}

function CollectionPill({
  item,
  refused,
  onRemove,
}: {
  item: ProductCollectionMembership;
  refused: boolean;
  /** Absent for automatic memberships and read-only staff. */
  onRemove?: (() => void) | undefined;
}) {
  const { t } = useTranslation('collections');
  const automatic = item.kind === 'automated';
  return (
    <li>
      <Badge
        variant={automatic ? 'outline' : 'secondary'}
        title={automatic ? t('membership.rulesHint') : item.handle}
        className={cn(
          'max-w-full gap-1',
          onRemove && 'pr-1',
          automatic && 'bg-primary/10 text-primary dark:bg-primary/20 border-transparent',
          refused && 'border-destructive/50 bg-destructive/10 text-destructive',
        )}
      >
        {automatic ? <Sparkles className="size-3" aria-hidden /> : null}
        {item.status === 'hidden' ? (
          <EyeOff className="size-3 opacity-70" aria-label={t('status.hidden')} />
        ) : null}
        <span className="truncate">{item.title}</span>
        {automatic ? <span className="sr-only">{t('membership.automatic')}</span> : null}
        {onRemove ? (
          <button
            type="button"
            className="hover:bg-foreground/10 rounded-full p-0.5"
            aria-label={t('membership.removeFrom', { title: item.title })}
            onClick={onRemove}
          >
            <X className="size-3" aria-hidden />
          </button>
        ) : null}
      </Badge>
    </li>
  );
}

function CollectionCombobox({
  draft,
  storeId,
  storeName,
}: {
  draft: ProductCollectionsDraft;
  storeId: string;
  /** Given, the trigger is a small Add beside this store's name rather than a full-width field. */
  storeName?: string;
}) {
  const { t } = useTranslation('collections');
  const [open, setOpen] = useState(false);
  const { manual, automated, selected, toggle } = draft;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {storeName === undefined ? (
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={t('membership.add')}
            className="text-muted-foreground w-full justify-between font-normal"
          >
            {t('membership.add')}
            <ChevronsUpDown className="opacity-50" aria-hidden />
          </Button>
        ) : (
          <Button
            type="button"
            variant="link"
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label={t('membership.addIn', { store: storeName })}
            className="h-auto shrink-0 p-0 text-[13px]"
          >
            {t('membership.addShort')}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'min-w-72 p-0',
          storeName === undefined && 'w-[var(--radix-popover-trigger-width)]',
        )}
        align={storeName === undefined ? 'start' : 'end'}
      >
        <Command>
          <CommandInput placeholder={t('membership.searchPlaceholder')} />
          <CommandList className="max-h-72">
            <CommandEmpty>{t('membership.noMatches')}</CommandEmpty>
            {manual.length > 0 ? (
              <CommandGroup heading={t('membership.manual')}>
                {manual.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    keywords={[item.title, item.handle]}
                    data-checked={selected.has(item.id)}
                    onSelect={() => toggle(item.id)}
                  >
                    <Check
                      className={cn('size-4', selected.has(item.id) ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{item.title}</span>
                      <span className="text-muted-foreground block truncate font-mono text-[11px]">
                        {item.handle}
                      </span>
                    </span>
                    {item.status === 'hidden' ? (
                      <Badge variant="outline" className="border-border text-muted-foreground">
                        {t('status.hidden')}
                      </Badge>
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {automated.length > 0 ? (
              <CommandGroup heading={t('membership.automated')}>
                {automated.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    keywords={[item.title, item.handle]}
                    disabled
                  >
                    <Check
                      className={cn('size-4', item.matchedByRules ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="flex-1 truncate">{item.title}</span>
                    <Sparkles className="text-primary size-3.5" aria-hidden />
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
          <CommandSeparator />
          <div className="flex flex-col p-1">
            <Button asChild variant="ghost" size="sm" className="justify-start">
              <Link to="/s/$storeId/collections/new" params={{ storeId }}>
                <Plus aria-hidden />
                {t('membership.create')}
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="justify-start">
              <Link to="/s/$storeId/collections" params={{ storeId }}>
                <Settings2 aria-hidden />
                {t('membership.manage')}
              </Link>
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** The product's collections in one store as pills, and why a save refused any of them. */
function MembershipPills({ draft }: { draft: ProductCollectionsDraft }) {
  const { t } = useTranslation(['collections', 'errors']);
  const message = useErrorMessage();
  const { manual, automated, selected, refused, canWrite, saving, error } = draft;
  const editable = canWrite && !saving;

  const pills = [
    ...manual.filter((i) => selected.has(i.id)),
    ...automated.filter((i) => i.matchedByRules),
  ];
  // A 422 names collections by index and marks their pills; anything else shows as it came.
  const refusedCode = pills.map((i) => refused.get(i.id)).find(Boolean);

  return (
    <>
      {pills.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('collections:membership.inList')}>
          {pills.map((item) => (
            <CollectionPill
              key={item.id}
              item={item}
              refused={refused.has(item.id)}
              onRemove={
                editable && item.kind === 'manual' ? () => draft.toggle(item.id) : undefined
              }
            />
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-xs">{t('collections:membership.none')}</p>
      )}

      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {refusedCode === undefined
              ? message(error)
              : refusedCode === 'INVALID_REFERENCE'
                ? t('collections:membership.refusedUnknown')
                : t('collections:membership.refusedAutomated')}
            {isApiError(error) && error.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
    </>
  );
}

function MembershipEditor({ draft, storeId }: { draft: ProductCollectionsDraft; storeId: string }) {
  const { t } = useTranslation('collections');
  return (
    <div className="flex flex-col gap-3">
      <MembershipPills draft={draft} />
      {draft.canWrite ? (
        <CollectionCombobox draft={draft} storeId={storeId} />
      ) : (
        <p className="text-muted-foreground text-xs">{t('membership.readOnly')}</p>
      )}
    </div>
  );
}

export interface ProductCollectionsStoreGroupProps {
  /** This store's own draft; it joins the save bar by itself. */
  draft: ProductCollectionsDraft;
  storeName: string;
}

/**
 * One store's collections inside the master page's Collections card: the store's name, an Add
 * that opens the picker, and the pills. A store the product is not in has nothing to show here.
 */
export function ProductCollectionsStoreGroup({
  draft,
  storeName,
}: ProductCollectionsStoreGroupProps) {
  const { t } = useTranslation('collections');
  const { query, storeId, canWrite } = draft;
  if (!storeId) return null;
  if (query.isSuccess && !query.data.published) return null;
  return (
    <div
      role="group"
      aria-label={storeName}
      className="flex flex-col gap-1.5 border-t pt-2 first:border-t-0 first:pt-0"
    >
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{storeName}</span>
        {draft.dirty ? (
          <span className="text-muted-foreground text-xs">{t('membership.unsaved')}</span>
        ) : null}
        {canWrite && query.isSuccess ? (
          <CollectionCombobox draft={draft} storeId={storeId} storeName={storeName} />
        ) : null}
      </div>
      {query.isPending ? (
        <Skeleton className="h-5 w-24 rounded-full" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : (
        <MembershipPills draft={draft} />
      )}
    </div>
  );
}

/**
 * Which collections of one store the product is in, for a side rail: memberships as pills, a
 * searchable combobox to add more. Manual memberships are a draft the page's Save writes;
 * automatic ones show as their rules decided.
 */
export function ProductCollectionsCard({
  draft,
  storeName,
  productId,
  storeSelector,
  onPublish,
  tour,
  badge,
}: ProductCollectionsCardProps) {
  const { t } = useTranslation('collections');
  const titleId = useId();
  const { query, storeId } = draft;

  let body: ReactNode;
  if (!storeId) body = null;
  else if (query.isPending) {
    body = (
      <div className="flex flex-col gap-2">
        <div className="flex gap-1.5">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
        <Skeleton className="h-9" />
      </div>
    );
  } else if (query.isError) {
    body = <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  } else if (!query.data.published) {
    body = <Unpublished storeName={storeName} productId={productId} onPublish={onPublish} />;
  } else if (query.data.items.length === 0) {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p className="text-muted-foreground text-xs">
          {t('membership.empty', { store: storeName })}
        </p>
        {draft.canWrite ? (
          <Button asChild size="sm" variant="outline">
            <Link to="/s/$storeId/collections/new" params={{ storeId }}>
              <Plus aria-hidden />
              {t('membership.create')}
            </Link>
          </Button>
        ) : null}
      </div>
    );
  } else {
    body = <MembershipEditor draft={draft} storeId={storeId} />;
  }

  // FormSection's look, plus a named region so the card can be found and announced.
  return (
    <Card role="region" aria-labelledby={titleId} data-tour={tour} className="gap-2.5 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center gap-2 text-[15px] leading-tight">
          <span id={titleId}>{t('membership.title')}</span>
          {badge}
          {draft.dirty ? (
            <span className="rounded-full border border-amber-500/40 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              {t('membership.unsaved')}
            </span>
          ) : null}
        </CardTitle>
        <CardDescription>{t('membership.description', { store: storeName })}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 px-4">
        {storeSelector}
        {body}
      </CardContent>
    </Card>
  );
}
